import { Col, Row, Tabs, Typography } from 'antd';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { BackButton } from 'src/components/BackButton/BackButton';
import { Button } from 'src/components/Button/Button';
import { DetailsPage } from 'src/components/FormPages/DetailsPage';
import { PartnerLoanApplicationStatusView } from 'src/components/PartnerLoanApplicationStatusView/PartnerLoanApplicationStatusView';
import { EntityLinks } from 'src/core/router/EntityLinks';
import { Router } from 'src/core/router/Router';
import { useLoadResources } from 'src/hooks/useLoadResources';
import { DeclinePartnerApplicationDialog } from 'src/pages/AgentLoanApplicationDetailsPage/components/DeclinePartnerApplicationDialog';
import { InitialApprovePartnerApplicationDialog } from 'src/pages/AgentLoanApplicationDetailsPage/components/InitialApprovePartnerApplicationDialog';
import { AgentLoanApplicaitonRepaymentsTab } from 'src/pages/AgentLoanApplicationDetailsPage/tabs/AgentLoanApplicaitonRepaymentsTab';
import { AgentLoanApplicationGeneralInfoTab } from 'src/pages/AgentLoanApplicationDetailsPage/tabs/AgentLoanApplicationGeneralInfoTab';
import { AgentLoanApplicationMerchantSnapshotTab } from 'src/pages/AgentLoanApplicationDetailsPage/tabs/AgentLoanApplicationMerchantSnapshotTab';
import { AgentLoanApplicationsStore } from 'src/stores/AgentLoanApplicationsStore/AgentLoanApplicationsStore';
import { AgentApplicationStatus } from 'src/stores/AgentLoanApplicationsStore/AgentLoanApplicationsStore.types';
import { InstallmentOptionResourceStore } from 'src/stores/Resources/InstallmentOptionResource';
import { PaymentFreequencyResourceStore } from 'src/stores/Resources/PaymentFreequencyResourceStore';
import { BasicStore } from 'src/utils/mobx/BasicStore/BasicStore';
import { Entity } from 'src/utils/mobx/BasicStore/BasicStore.types';

// eslint-disable-next-line complexity
export const AgentLoanApplicationDetailsPage = observer(() => {
    const { isLoadingResources } = useLoadResources([
        InstallmentOptionResourceStore,
        PaymentFreequencyResourceStore,
    ]);

    const application = AgentLoanApplicationsStore.currentItem;

    const showApproveButton =
        application && application.status === AgentApplicationStatus.ASSIGNED;

    const showDeclineButton =
        application && application.status === AgentApplicationStatus.ASSIGNED;

    const loanApplicationsListUrl = EntityLinks.loanApplications.list?.();

    return (
        <DetailsPage
            pageName="agent-loan-application-details"
            store={AgentLoanApplicationsStore as unknown as BasicStore<Entity>}
            isLoading={isLoadingResources}
        >
            <Row gutter={[16, 16]} align="middle">
                <Col span={24}>
                    <BackButton
                        to={loanApplicationsListUrl}
                        text={t('Agent.LoanApplication.BackToApplications')}
                    />
                </Col>
                <Col flex={1}>
                    <Typography.Title level={3}>
                        {application?.number}
                    </Typography.Title>
                </Col>

                {application?.status && (
                    <Col>
                        <PartnerLoanApplicationStatusView
                            status={application.status}
                        />
                    </Col>
                )}

                {showApproveButton && (
                    <Col>
                        <Button
                            type="primary"
                            onClick={() => {
                                InitialApprovePartnerApplicationDialog.render({
                                    applicationId: application.id!,
                                    onSuccess: () => {
                                        AgentLoanApplicationsStore.loadItem(
                                            application.id!,
                                        );
                                    },
                                });
                            }}
                        >
                            {t('Agent.LoanApplication.Actions.Approve')}
                        </Button>
                    </Col>
                )}

                {showDeclineButton && (
                    <Col>
                        <Button
                            className="btn-danger"
                            onClick={() => {
                                DeclinePartnerApplicationDialog.render({
                                    applicationId: application.id!,
                                    onSuccess: () => {
                                        loanApplicationsListUrl &&
                                            Router.navigate(
                                                loanApplicationsListUrl,
                                            );
                                    },
                                });
                            }}
                        >
                            {t('Agent.LoanApplication.Actions.Decline')}
                        </Button>
                    </Col>
                )}

                <Col span={24}>
                    <Tabs
                        items={[
                            {
                                key: 'agent-general-information',
                                label: t(
                                    'Agent.LoanApplication.Tab.GeneralInformation',
                                ),
                                children: (
                                    <AgentLoanApplicationGeneralInfoTab
                                        application={application}
                                    />
                                ),
                            },
                            {
                                key: 'agent-merchant-snapshot',
                                label: t(
                                    'Agent.LoanApplication.Tab.MerchantSnapshot',
                                ),
                                children: (
                                    <AgentLoanApplicationMerchantSnapshotTab />
                                ),
                            },
                            {
                                key: 'agent-repayments',
                                label: t(
                                    'Agent.LoanApplication.Tab.Repayments',
                                ),
                                children: (
                                    <AgentLoanApplicaitonRepaymentsTab
                                        application={application}
                                    />
                                ),
                            },
                        ]}
                    />
                </Col>
            </Row>
        </DetailsPage>
    );
});
